import React from "react";
import { Box, Card, Typography } from "@mui/material";
import AccessTime from "@mui/icons-material/AccessTime";
import PersonOutline from "@mui/icons-material/PersonOutline";
import { useZoomPlayerStyles } from "./style";
import { formatTime } from "./utils";

type TranscriptLine = {
  start: number;
  end: number;
  text: string;
  speaker: string;
};

type TranscriptPanelProps = {
  transcript: TranscriptLine[];
  currentTime: number;
  seekingToTime: number | null;
  onSeek: (line: TranscriptLine) => void;
  transcriptRef: React.RefObject<HTMLDivElement | null>;
  onUserInteract?: () => void;
};

export const TranscriptPanel: React.FC<TranscriptPanelProps> = ({
  transcript,
  currentTime,
  seekingToTime,
  onSeek,
  transcriptRef,
  onUserInteract,
}) => {
  const classes = useZoomPlayerStyles();

  const handleScroll = (e: React.UIEvent) => {
    e.stopPropagation();
    onUserInteract?.();
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.stopPropagation();
    onUserInteract?.();
  };

  return (
    <Box
      className={classes.transcriptScroll}
      ref={transcriptRef}
      onWheel={handleWheel}
      onTouchStart={onUserInteract}
      onScroll={handleScroll}
    >
      {transcript.map((line, index) => {
        const isActive = currentTime >= line.start && currentTime <= line.end;
        const isSeeking =
          seekingToTime !== null && seekingToTime === line.start;
        const itemClass = `${classes.transcriptItem} ${
          isActive ? classes.transcriptItemActive : ""
        } ${isSeeking ? classes.transcriptItemSeeking : ""}`;
        const textClass = isActive
          ? classes.transcriptTextActive
          : classes.transcriptText;

        return (
          <Box p={1} bgcolor="#F5F5F5" key={`${line.start}-${index}`}>
            <Card sx={{ p: 0 }}>
              <Box
                className={itemClass}
                onClick={() => onSeek(line)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") onSeek(line);
                }}
              >
                <Box
                  className={classes.rowCenterGap8Mb4}
                  sx={{ justifyContent: "space-between" }}
                >
                  <Box className={classes.speakerRow}>
                    <PersonOutline sx={{ fontSize: 22, color: "#616161" }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      {line.speaker}
                    </Typography>
                  </Box>
                  <Typography
                    className={classes.timeCaption}
                    fontWeight={500}
                    variant="caption"
                    display="flex"
                    alignItems="center"
                    gap={0.4}
                  >
                    <AccessTime sx={{ fontSize: 16 }} />
                    {formatTime(line.start)}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {line.text.replace(/^[^:]+:\s*/, "")}
                </Typography>
              </Box>
            </Card>
          </Box>
        );
      })}
    </Box>
  );
};
